import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
const POSTS_PER_PAGE = 30;

export const fetchPostsByUserId = createAsyncThunk(
  'posts/fetchPostsByUserId',
  async ({ userPosts, userId }, { rejectWithValue }) => {
    try {
      return { userPosts, userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for adding a new post
export const addNewPost = createAsyncThunk(
  'posts/addNewPost',
  async ({ newPost }, { rejectWithValue }) => {
    try {
      return newPost
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a post
export const deletePostById = createAsyncThunk(
  'posts/deletePost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      return { postId, success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a post
export const updatePostById = createAsyncThunk(
  'posts/updatePostById',
  async ({ updatedPost }, { rejectWithValue }) => {
    return updatedPost;
  }
);

// Async thunk for fetching initial posts
export const fetchInitialPosts = createAsyncThunk(
  'posts/fetchInitialPosts',
  async (data, { rejectWithValue }) => {
    try {
      const postOrder = data.data?.map(post => post.id) || [];
      const posts = data.data.reduce((acc, post) => {
        acc[post.id] = post;
        return acc;
      }, {});

      const result = {
        posts,
        postOrder,
      };
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching next posts (pagination)
export const fetchNextPosts = createAsyncThunk(
  'posts/fetchNextPosts',
  async ({ data, nextPage }, { rejectWithValue }) => {
    try {
      const postNumbers = data.data?.length;
      const posts = data.data.reduce((acc, post) => {
        acc[post.id] = post;
        return acc;
      }, {});
      const postOrder = data.data?.map(post => post.id) || [];
      const hasMore = postNumbers === POSTS_PER_PAGE;

      return {
        posts,
        postOrder,
        nextPage,
        hasMore
      };
    } catch (error) {
      console.error("Error in fetchNextPosts:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching a single post
export const fetchPostById = createAsyncThunk(
  'posts/fetchPostById',
  async (data, { rejectWithValue }) => {
    try {
      const postData = data.data;
      if (postData) {
        return {
          ...postData
        };
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Update only the comment count of a post using runTransaction, increment or decrement based on isCommentAdded
export const updateCommentCount = createAsyncThunk(
  'posts/updateCommentCount',
  async ({ postId, isCommentAdded }, { rejectWithValue }) => {
    try {
      return { postId, isCommentAdded };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for toggling like on a post
export const toggleLikePost = createAsyncThunk(
  'posts/toggleLikePost',
  async ({ postId, isLiked }, { rejectWithValue }) => {
    try {
      return {
        postId,
        isLiked
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for disabling comments on a post
export const toggleCommentsDisabled = createAsyncThunk(
  'posts/toggleCommentsDisabled',
  async ({ postId, currentStatus }, { rejectWithValue }) => {
    try {
      const currentUserId = localStorage.getItem("userId");
      return { postId, commentsDisabled: currentStatus ? 0 : 1, currentUserId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for handling poll votes
export const handlePollVote = createAsyncThunk(
  'posts/handlePollVote',
  async ({ postId, updatedOptions }, { rejectWithValue }) => {
    try {
      return { postId, updatedOptions };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchNextUserPosts = createAsyncThunk(
  'posts/fetchNextUserPosts',
  async ({ userPosts, userId }, { rejectWithValue }) => {
    try {
      const userPostData = userPosts.slice().reverse().reduce((acc, post) => {
        acc[post.id] = post;
        return acc;
      }, {});
      return { userPostData, userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const postSlice = createSlice({
  name: 'posts',
  initialState: {
    entities: {},
    postOrder: [],
    nextPage: 1,
    hasMore: true,
    loading: false,
    singlePostLoading: {},
    error: null,
    userPosts: {},
    activeMediaIndices: {},
    reportStatus: {},
  }, reducers: {
    setActiveMediaIndex: (state, action) => {
      const { postId, index } = action.payload;
      if (!state.activeMediaIndices) state.activeMediaIndices = {};
      state.activeMediaIndices[postId] = index;
    },
    clearPosts: (state) => {
      state.loading = true;
      state.entities = {};
      state.postOrder = [];
      state.nextPage = 1;
      state.hasMore = true;
      // Keep other state properties intact
    }
  },
  extraReducers: (builder) => {
    builder
      // Add New Post
      .addCase(addNewPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewPost.fulfilled, (state, action) => {
        const newPost = action.payload;
        // Add the new post to entities
        state.entities = { ...state.entities, [newPost.id]: newPost };
        state.postOrder = [newPost.id, ...state.postOrder];
        state.loading = false;
        state.error = null;
      })
      .addCase(addNewPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePostById?.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePostById?.fulfilled, (state, action) => {
        const updatedPost = action.payload;
        const postId = updatedPost.id;

        // Update the post in entities (Map)
        if (state.entities[postId]) {
          state.entities[postId] = {
            ...updatedPost,
          };
        }

        // Also update in userPosts if the post exists there
        // Loop through all user IDs in userPosts
        Object.keys(state.userPosts || {}).forEach(userId => {
          if (state.userPosts[userId] && state.userPosts[userId][postId]) {
            state.userPosts[userId][postId] = {
              ...updatedPost,
            };
          }
        });

        state.loading = false;
        state.error = null;
      })
      .addCase(updatePostById?.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Initial Posts
      .addCase(fetchInitialPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInitialPosts.fulfilled, (state, action) => {
        state.entities = action.payload.posts;
        state.postOrder = action.payload.postOrder;
        state.nextPage = 2;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchInitialPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Next Posts (Pagination)
      .addCase(fetchNextPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNextPosts.fulfilled, (state, action) => {
        // If action.payload.posts is already a Map
        state.entities = { ...state.entities, ...action.payload.posts };
        state.postOrder = [...state.postOrder, ...action.payload.postOrder];
        state.hasMore = action.payload.hasMore;
        state.nextPage = action.payload.nextPage + 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNextPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchNextUserPosts.fulfilled, (state, action) => {
        const { userPostData, userId } = action.payload;
        state.userPosts[userId] = { ...state.userPosts[userId], ...userPostData };
        state.loading = false;
      })

      // Fetch Post By ID
      .addCase(fetchPostById.pending, (state, action) => {
        const { postId } = action.meta.arg;
        state.singlePostLoading = { ...state.singlePostLoading, [postId]: true };
        state.error = null;
      })
      .addCase(fetchPostById.fulfilled, (state, action) => {
        const post = action?.payload;
        if (!post?.id) {
          state.singlePostLoading[post] = false;
          return;
        }
        state.entities[post.id] = post;
        state.singlePostLoading[post.id] = false;
      })
      .addCase(fetchPostById.rejected, (state, action) => {
        const postId = action.meta.arg;
        state.singlePostLoading[postId] = false;
        state.error = action.payload;
      })
      .addCase(toggleLikePost.fulfilled, (state, action) => {
        const { postId, isLiked } = action.payload;
        // Get the post from Map
        const post = state.entities[postId];
        if (post) {
          // Update optimistic isLiked flag
          post.user_has_liked = isLiked === 1;
          const count = Number(post.like_count);
          // Update like count
          post.like_count = isLiked === 1 ? (count || 0) + 1 : Math.max(0, (count || 0) - 1);
          // Set the updated post back (optional since objects are passed by reference)
          state.entities[postId] = post;
        }
        // Handle userPosts as before
        if (state.userPosts) {
          Object.values(state.userPosts).forEach((userPostCollection) => {
            if (userPostCollection[postId]) {
              const userPost = userPostCollection[postId];
              userPost.user_has_liked = isLiked === 1;
              const count = Number(userPost.like_count);
              userPost.like_count = isLiked === 1 ? (count || 0) + 1 : Math.max(0, (count || 0) - 1);
            }
          });
        }
      })
      .addCase(updateCommentCount.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCommentCount.fulfilled, (state, action) => {
        const { postId, isCommentAdded } = action.payload;
        if (state.entities[postId]) {
          state.entities[postId].comment_count = isCommentAdded ? Number(state.entities[postId].comment_count) + 1 : Number(state.entities[postId].comment_count) - 1;
        }
      })
      // Toggle Comments Disabled
      .addCase(toggleCommentsDisabled.fulfilled, (state, action) => {
        const { postId, commentsDisabled, currentUserId } = action.payload;
        state.entities[postId].comments_disabled = commentsDisabled;
        if (state.userPosts[currentUserId]) {
          state.userPosts[currentUserId][postId].comments_disabled = commentsDisabled;
        }

      })
      // Handle Poll Vote
      .addCase(handlePollVote.fulfilled, (state, action) => {
        const { postId, updatedOptions } = action.payload;
        // Update in entities if the post exists and has a poll
        if (state.entities && state.entities[postId] && state.entities[postId].poll) {
          state.entities[postId].poll.opts = updatedOptions;
        }
        // Also update in userPosts if the post exists there and has a poll
        if (state.userPosts && state.userPosts[postId] && state.userPosts[postId].poll) {
          state.userPosts[postId].poll.opts = updatedOptions;
        }
      })
      .addCase(fetchPostsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostsByUserId.fulfilled, (state, action) => {
        const { userId, userPosts } = action.payload;
        const postData = userPosts.reduce((acc, post) => {
          acc[post.id] = post;
          return acc;
        }, {});
        // Even if userPosts is empty, we need to set it to an empty object (not undefined)
        // This indicates we've attempted to fetch posts for this user
        state.userPosts[userId] = postData;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPostsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Post
      .addCase(deletePostById.fulfilled, (state, action) => {
        const { postId } = action.payload;

        // Remove from main entities state using Map.delete()
        if (state.entities[postId]) {
          delete state.entities[postId];
          state.postOrder = state.postOrder.filter(id => id !== postId);
        }

        // Also remove from userPosts if it exists in any user's posts
        Object.keys(state.userPosts || {}).forEach(userId => {
          if (state.userPosts[userId] && state.userPosts[userId][postId]) {
            delete state.userPosts[userId][postId];
          }
        });
      });
  }
});

export const { setActiveMediaIndex, clearPosts, replaceAll } = postSlice.actions;
export default postSlice.reducer;